// Libs
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
// Components, Layouts, Pages
// Others
import useUserLocation from '~/utils/hooks/useUserLocation';
import { IBasePlaceGoogleMap, IBasePosition } from '~/utils/interface/common';
import { DEFAULT_MAP_ZOOM, DEFAULT_NUMBER_ZERO, DEFAULT_POSITION } from '~/utils/constants/common';
// Styles, images, icons
import styles from './BaseMap.module.scss';

type Props = {
  position?: IBasePosition | null;
  places?: IBasePlaceGoogleMap[] | null;
  width?: number | string;
  height?: number | string;
  triggerLocate?: boolean;
  onGetPosition?: (value: IBasePosition) => void;
};

const cx = classNames.bind(styles);

const BaseMap = (props: Props) => {
  //#region Destructuring Props
  const { position, places, width, height, triggerLocate = false, onGetPosition } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { userLocation, getUserLocation } = useUserLocation();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [positionSelected, setPositionSelected] = useState<IBasePosition>();
  const [activeMarker, setActiveMarker] = useState<number | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!triggerLocate) return;

    getUserLocation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerLocate]);

  useEffect(() => {
    if (!position) {
      const defaultPosition: IBasePosition =
        triggerLocate && userLocation
          ? { lat: userLocation?.latitude, lng: userLocation?.longitude }
          : DEFAULT_POSITION;

      setPositionSelected(defaultPosition);
      onGetPosition && onGetPosition(defaultPosition);
    }

    if (position) {
      setPositionSelected(position);
      onGetPosition && onGetPosition(position);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, userLocation]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseMarker = () => {
    setActiveMarker(null);
  };

  const handleActiveMarker = (markerId: number) => {
    if (markerId === activeMarker) {
      return;
    }
    setActiveMarker(markerId);
  };

  const handleOnLoad = (map: google.maps.Map) => {
    if (!places) return;

    const bounds = new google.maps.LatLngBounds();
    places.forEach(({ position }) => bounds.extend(position));
    map.fitBounds(bounds);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='baseMapComponent' className={cx('container')} style={{ width, height }}>
      <GoogleMap
        {...(Array.isArray(places) && places?.length > DEFAULT_NUMBER_ZERO && { onLoad: handleOnLoad })}
        mapContainerStyle={{ width: '100%', height: '100%', borderRadius: '8px' }}
        center={positionSelected}
        zoom={DEFAULT_MAP_ZOOM}
        onClick={handleCloseMarker}
      >
        {positionSelected && !places && <Marker position={positionSelected} />}

        {Array.isArray(places) &&
          places?.length > DEFAULT_NUMBER_ZERO &&
          places.map(({ id, position }) => {
            return <Marker key={id} position={position} onClick={() => handleActiveMarker(id)}></Marker>;
          })}
      </GoogleMap>
    </Box>
  );
};

export default BaseMap;
